import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Link,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Avatar,
  AvatarGroup,
  FormControl,
  Select,
  MenuItem,
  TablePagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Collapse,
} from "@mui/material";
import {
  Search,
  ArrowBackIosNew,
  Visibility,
  CheckCircle,
  HighlightOff,
  Close,
  LocationOn,
  InsertDriveFile,
  FolderZip,
  Verified,
  ChevronRight,
  DescriptionOutlined,
  ImageOutlined,
} from "@mui/icons-material";
import AdminAssetManagementSkeleton from "./AdminAssetManagementSkeleton";
import { useNavigate } from "react-router-dom";
import {
  useGetAdminAssetsQuery,
  useApproveAdminAssetMutation,
  useRejectAdminAssetMutation,
} from "../../../redux/api/authApi";
import { RejectAssetModal, getRejectDocuments } from "./RejectAssetModal";

const ALL_CATEGORIES = "All Categories";
const ALL_STATUS = "All Status";

/** Figma-aligned status labels */
const STATUS = {
  UNDER_REVIEW: "Pending",
  LIVE: "Live",
  REJECTED: "Rejected",
  FUNDED: "Funded",
  APPROVED: "Approved",
};

const STATUS_CHIP_KEY = {
  [STATUS.UNDER_REVIEW]: "pending",
  [STATUS.FUNDED]: "success",
  [STATUS.LIVE]: "success",
  [STATUS.APPROVED]: "success",
  [STATUS.REJECTED]: "error",
};

/** Map UI filter chip → `status` query param for GET /api/admin/assets */
function statusToApiQueryParam(uiStatus) {
  if (uiStatus === STATUS.UNDER_REVIEW) return "Pending";
  if (uiStatus === STATUS.LIVE) return "Live";
  if (uiStatus === STATUS.FUNDED) return "Funded";
  if (uiStatus === STATUS.APPROVED) return "Approved";
  if (uiStatus === STATUS.REJECTED) return "Rejected";
  return uiStatus;
}

function getListAndMeta(response) {
  const body = response;
  const d = body?.data;
  const p = body?.pagination && typeof body.pagination === "object" ? body.pagination : null;

  if (Array.isArray(d)) {
    return {
      list: d,
      total: Number(p?.total ?? d.length) || 0,
      page: Number(p?.page ?? 1) || 1,
      limit: Number(p?.limit ?? 10) || 10,
    };
  }

  if (d && typeof d === "object") {
    const list =
      d.assets ||
      d.items ||
      d.results ||
      d.docs ||
      (Array.isArray(d.data) ? d.data : null) ||
      [];
    const total =
      Number(
        d.total ?? d.totalCount ?? d.count ?? p?.total ?? body?.total ?? list.length
      ) || 0;
    const page = Number(d.page ?? d.currentPage ?? p?.page ?? body?.page ?? 1) || 1;
    const limit = Number(d.limit ?? d.perPage ?? p?.limit ?? body?.limit ?? 10) || 10;
    return {
      list: Array.isArray(list) ? list : [],
      total,
      page,
      limit,
    };
  }

  return { list: [], total: 0, page: 1, limit: 10 };
}

function normalizeUiStatus(status) {
  const s = String(status || "").toLowerCase().trim();
  if (s.includes("funded")) return STATUS.FUNDED;
  if (s.includes("reject")) return STATUS.REJECTED;
  if (s.includes("approved")) return STATUS.APPROVED;
  // Approved
  if (
    s.includes("live") ||
    s.includes("deploy") ||
    s === "active"
  ) {
    return STATUS.LIVE;
  }
  /* Pending, Under Review, etc. */
  return STATUS.UNDER_REVIEW;
}

function mapAssetRow(a) {
  const id = a?._id || a?.id;
  const name = a?.assetName || a?.name || a?.title || "—";
  const ownerObj = typeof a?.owner === "object" && a.owner ? a.owner : null;
  const owner =
    (ownerObj && (ownerObj.name || ownerObj.fullName)) ||
    a?.ownerName ||
    (typeof a?.owner === "string" ? a.owner : null) ||
    "—";
  const ownerAvatar =
    ownerObj?.avatar || ownerObj?.profileImage || a?.ownerAvatar || a?.ownerPhoto;
  const ownerOthers = a?.ownerOthersCount ?? a?.coOwnersCount ?? 0;

  const category =
    (typeof a?.category === "object" &&
      a.category &&
      (a.category.name || a.category.title)) ||
    a?.categoryName ||
    (typeof a?.category === "string" ? a.category : null) ||
    "—";
  const value =
    a?.value ??
    a?.totalAssetValue ??
    a?.valuation ??
    a?.financialDetails?.totalAssetValue ??
    0;
  let location =
    a?.location ||
    [
      a?.basicInfo?.completeAddress?.city,
      a?.basicInfo?.completeAddress?.country,
    ]
      .filter(Boolean)
      .join(", ") ||
    [a?.city, a?.state, a?.country].filter(Boolean).join(", ") ||
    a?.completeAddress?.city ||
    "—";
  if (location === "-") location = "—";
  const rawStatus = a?.status || a?.assetStatus || "Pending";
  const uiStatus = normalizeUiStatus(rawStatus);
  const rawDate =
    a?.submitted || a?.submittedAt || a?.createdAt || a?.updatedAt || a?.date;
  const date = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : "—";
  const investorsCount =
    a?.investorCount ?? a?.investorsCount ?? a?.others ?? a?.investorTotal ?? 0;
  const investors = Array.isArray(a?.investors)
    ? a.investors.map((inv) => ({
        userId: inv?.userId ?? inv?._id,
        name: inv?.name != null ? String(inv.name) : "",
        email: inv?.email != null ? String(inv.email) : "",
      }))
    : [];

  const documentCount = Array.isArray(a?.documents)
    ? a.documents.length
    : a?.documentCount ??
    (Array.isArray(a?.documentation) ? a.documentation.length : 0);

  return {
    id,
    name,
    owner,
    ownerAvatar,
    ownerOthers,
    category,
    value: typeof value === "number" ? value : Number(value) || 0,
    location,
    status: uiStatus,
    rawStatus,
    date,
    investorsCount,
    investors,
    documentCount,
    raw: a,
  };
}

function formatSar(v) {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "SAR 0";
  const hasDecimals = Math.abs(n % 1) > 1e-9;
  return (
    "SAR " +
    new Intl.NumberFormat("en-SA", {
      maximumFractionDigits: hasDecimals ? 2 : 0,
      minimumFractionDigits: 0,
    }).format(n)
  );
}

/** First letter of given name + first letter of family name (last word). E.g. Parul Verma → PV, Praul Investor → PI. */
function ownerInitials(name) {
  if (!name || name === "—") return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  const one = parts[0] || "";
  if (one.length >= 2) return one.slice(0, 2).toUpperCase();
  return one ? one[0].toUpperCase() : "?";
}

function investorListInitials(inv) {
  if (!inv || typeof inv !== "object") return "?";
  const name = String(inv.name ?? "").trim();
  if (name) return ownerInitials(name);
  const email = String(inv.email ?? "").trim();
  if (email.length >= 2) return email.slice(0, 2).toUpperCase();
  return "?";
}

function StatusBadge({ status }) {
  const key = STATUS_CHIP_KEY[status] || "pending";
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        height: 24,
        px: 0.5,
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
        borderRadius: 999,
      }}
    />
  );
}

function CategoryBadge({ label }) {
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        fontSize: 12,
        fontWeight: 600,
        borderColor: "border.light",
        color: "text.neutral",
        bgcolor: "background.subtle",
        borderRadius: 999,
      }}
    />
  );
}

function InvestorsCell({ count, investors = [] }) {
  if (!count || count <= 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.muted" }} fontSize={14}>
        No investors
      </Typography>
    );
  }
  const list = Array.isArray(investors) ? investors : [];
  const visible = list.slice(0, Math.min(3, count));
  const othersExtra = Math.max(0, count - 3);
  const avatarSx = {
    bgcolor: (theme) => theme.palette.statIcon.revenue.bg,
    color: (theme) => theme.palette.statIcon.revenue.color,
    width: 28,
    height: 28,
  };
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <AvatarGroup
        max={3}
        sx={{
          "& .MuiAvatar-root": {
            width: 28,
            height: 28,
            fontSize: 12,
            border: (theme) => `2px solid ${theme.palette.background.paper}`,
          },
        }}
      >
        {visible.length > 0 ? (
          visible.map((inv, i) => (
            <Avatar
              key={inv.userId || inv.email || `inv-${i}`}
              title={inv.name || inv.email || undefined}
              sx={avatarSx}
            >
              {investorListInitials(inv)}
            </Avatar>
          ))
        ) : count > 0 ? (
          <Avatar
            title={`${count} investor${count === 1 ? "" : "s"}`}
            sx={{ ...avatarSx, fontSize: 11 }}
          >
            {count > 99 ? "99+" : count}
          </Avatar>
        ) : null}
      </AvatarGroup>
      {othersExtra > 0 ? (
        <Typography variant="body2" fontSize={13} sx={{ color: "text.muted" }}>
          +{othersExtra} others
        </Typography>
      ) : null}
    </Stack>
  );
}

function formatUploadedDocSubtitle(d) {
  const datePart = d.date && d.date !== "—" ? `Uploaded ${d.date}` : "Uploaded —";
  return `${d.category} • ${d.size} • ${datePart}`;
}

function previewUploadedDocument(doc) {
  const url = doc.url;
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function AssetReviewModal({ open, onClose, asset, onVerify, onRejectClick }) {
  const [uploadedDocsOpen, setUploadedDocsOpen] = useState(false);

  const docsList = useMemo(() => (asset ? getRejectDocuments(asset) : []), [asset]);

  useEffect(() => {
    if (!open) setUploadedDocsOpen(false);
  }, [open, asset?.id]);

  if (!asset) return null;

  const docCount = docsList.length || asset.documentCount || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography fontWeight={700} fontSize={20}>
              Asset Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Review asset details and documentation
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "border.subtle" }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={asset.ownerAvatar} sx={{ width: 40, height: 40, fontSize: 16 }}>
              {ownerInitials(asset.owner)}
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Owner
              </Typography>
              <Typography fontWeight={700} fontSize={16}>
                {asset.owner}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <LocationOn sx={{ color: (theme) => theme.palette.accent.teal.action, mt: 0.25 }} fontSize="small" />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Asset
              </Typography>
              <Typography fontWeight={700} fontSize={18}>
                {asset.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {asset.location}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              pt: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Category
              </Typography>
              <Typography fontWeight={600} mt={0.5}>
                {asset.category}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Value
              </Typography>
              <Typography fontWeight={600} mt={0.5}>
                {formatSar(asset.value)}
              </Typography>
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Documents
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                <InsertDriveFile sx={{ color: "primary.main", fontSize: 20 }} />
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => setUploadedDocsOpen((v) => !v)}
                  sx={{
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.25,
                    cursor: "pointer",
                    border: "none",
                    background: "none",
                    padding: 0,
                    font: "inherit",
                    color: "primary.main",
                  }}
                >
                  {docCount} files
                  <ChevronRight
                    sx={{
                      fontSize: 20,
                      transition: "transform 0.2s",
                      transform: uploadedDocsOpen ? "rotate(90deg)" : "rotate(0deg)",
                    }}
                  />
                </Link>
              </Stack>
            </Box>
          </Box>

          <Collapse in={uploadedDocsOpen} timeout="auto" unmountOnExit>
            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                borderRadius: 2,
                borderColor: "border.light",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: "background.subtle",
                  borderBottom: (theme) => `1px solid ${theme.palette.border.subtle}`,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DescriptionOutlined sx={{ color: "primary.main", fontSize: 22 }} />
                  <Typography fontWeight={700} fontSize={15}>
                    Uploaded Documents
                  </Typography>
                </Stack>
                <IconButton size="small" onClick={() => setUploadedDocsOpen(false)} aria-label="Close documents">
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
              <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                {docsList.length === 0 ? (
                  <Typography sx={{ p: 2.5 }} color="text.secondary" variant="body2">
                    No uploaded documents.
                  </Typography>
                ) : (
                  docsList.map((d, idx) => (
                    <Stack
                      key={d.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: (theme) =>
                          idx < docsList.length - 1
                            ? `1px solid ${theme.palette.border.subtle}`
                            : "none",
                        "&:hover": {
                          bgcolor: (theme) => theme.palette.table.rowHover,
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                        {d.kind === "zip" ? (
                          <FolderZip sx={{ color: "stats.amber", flexShrink: 0 }} />
                        ) : d.kind === "image" ? (
                          <ImageOutlined sx={{ color: "stats.green", flexShrink: 0 }} />
                        ) : (
                          <InsertDriveFile sx={{ color: "text.faint", flexShrink: 0 }} />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} fontSize={14} noWrap>
                            {d.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatUploadedDocSubtitle(d)}
                          </Typography>
                        </Box>
                      </Stack>
                      <IconButton
                        size="small"
                        aria-label={`Preview ${d.name}`}
                        onClick={() => previewUploadedDocument(d)}
                        sx={{
                          color: (theme) => theme.palette.accent.teal.action,
                          flexShrink: 0,
                          "&:hover": {
                            bgcolor: (theme) => theme.palette.accent.teal.chipBg,
                          },
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))
                )}
              </Box>
            </Paper>
          </Collapse>

          {asset.status === STATUS.LIVE ? (
            <Alert
              severity="success"
              icon={false}
              sx={{
                mt: 1,
                bgcolor: (theme) => theme.palette.chips.success.bg,
                color: (theme) => theme.palette.chips.success.text,
                fontWeight: 600,
                borderRadius: 2,
                "&.MuiAlert-root": { py: 1.25 },
              }}
            >
              This asset is actively trading on the platform.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      {asset.status !== STATUS.LIVE ? (
        <DialogActions sx={{ p: 2.5, gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<Verified />}
            onClick={() => onVerify?.(asset)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "primary.main",
              px: 2,
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            Verify Asset (Ready for Deploy)
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<HighlightOff />}
            onClick={() => onRejectClick?.(asset)}
            sx={{ textTransform: "none", fontWeight: 700, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
          >
            Reject Asset
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

export default function AssetManagement() {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [status, setStatus] = useState(ALL_STATUS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [reviewAsset, setReviewAsset] = useState(null);
  const [rejectAsset, setRejectAsset] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [rejectError, setRejectError] = useState(null);
  const navigate = useNavigate();

  const [approveAsset, { isLoading: approveMutationLoading }] = useApproveAdminAssetMutation();
  const [rejectAssetMutation] = useRejectAdminAssetMutation();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, category, status]);

  const categoryParam = category === ALL_CATEGORIES ? "" : category;
  const statusParam = status === ALL_STATUS ? "" : statusToApiQueryParam(status);

  const queryArg = useMemo(
    () => ({
      search: debouncedSearch,
      category: categoryParam,
      status: statusParam,
      page,
      limit,
    }),
    [debouncedSearch, categoryParam, statusParam, page, limit]
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useGetAdminAssetsQuery(
    queryArg,
    { refetchOnMountOrArgChange: true },
  );

  const { list: rawList, total } = useMemo(() => getListAndMeta(data), [data]);
  const rows = useMemo(() => rawList.map(mapAssetRow), [rawList]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.category && r.category !== "—") set.add(r.category);
    });
    return Array.from(set).sort();
  }, [rows]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.status) set.add(r.status);
    });
    return Array.from(set).sort();
  }, [rows]);

  const showTableLoader = isLoading || (isFetching && !rawList.length);

  const openReview = (row) => setReviewAsset(row);
  const closeReview = () => setReviewAsset(null);

  const openReject = (row) => {
    setRejectError(null);
    setRejectAsset(row);
    setReviewAsset(null);
  };
  const closeReject = () => setRejectAsset(null);

  /** TODO: wire useVerifyAdminAssetMutation / useRejectAdminAssetMutation when APIs are ready */
  const handleVerify = () => {
    closeReview();
  };

  const handleRejectConfirm = async (asset, payload) => {
    const assetId = asset?.id ?? asset?.raw?._id ?? asset?.raw?.id;
    if (assetId == null || String(assetId).trim() === "") {
      setRejectError("Missing asset id.");
      throw new Error("Missing asset id");
    }
    const filesNeedToBeUpdated = Array.isArray(payload?.filesNeedToBeUpdated)
      ? payload.filesNeedToBeUpdated.map((x) => String(x))
      : [];
    setRejectError(null);
    try {
      await rejectAssetMutation({
        assetId: String(assetId).trim(),
        rejectionReason: String(payload?.reason ?? ""),
        filesNeedToBeUpdated,
      }).unwrap();
      await refetch();
    } catch (e) {
      const msg = e?.data?.message || e?.error || "Could not reject asset.";
      setRejectError(msg);
      throw e;
    }
  };

  const handleQuickApprove = async (row) => {
    const assetId = row?.id ?? row?.raw?._id ?? row?.raw?.id;
    if (assetId == null || String(assetId).trim() === "") return;
    const idStr = String(assetId).trim();
    setApproveError(null);
    try {
      await approveAsset({ assetId: idStr }).unwrap();
      await refetch();
    } catch (e) {
      setApproveError(e?.data?.message || e?.error || "Could not approve asset.");
    }
  };

  return (
    <Box>
      {/* Back link */}
      <Button
        onClick={() => navigate("/admin")}
        startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to Dashboard
      </Button>

      {/* Page header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary", letterSpacing: -0.5 }}>
            Asset Management
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
            Review and approve asset listings
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<Download />}
          sx={{
            textTransform: "none",
            fontSize: 13,
            bgcolor: (theme) => theme.palette.accent.teal.action,
            borderRadius: 1,
            px: 2.5,
            py: 1,
            "&:hover": {
              bgcolor: (theme) => theme.palette.accent.teal.actionHover,
            },
          }}
        >
          Export Assets
        </Button> */}
      </Stack>

      {approveError ? (
        <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setApproveError(null)}>
          {approveError}
        </Alert>
      ) : null}

      {rejectError ? (
        <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setRejectError(null)}>
          {rejectError}
        </Alert>
      ) : null}

      {isError ? (
        <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => refetch()}>
          {error?.data?.message || error?.error || "Failed to load assets."}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 1.5,
          overflow: "hidden",
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
        }}
      >
        {/* Card header: Asset Listings + filters */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          sx={{ px: 3, pt: 3, pb: 2 }}
        >
          <Typography fontSize={16} fontWeight={700} sx={{ color: "text.primary" }}>
            Asset Listings
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search assets..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "text.muted" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  bgcolor: "background.paper",
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value={ALL_CATEGORIES}>{ALL_CATEGORIES}</MenuItem>
                {categoryOptions.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value={ALL_STATUS}>{ALL_STATUS}</MenuItem>
                {statusOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Divider />

        {showTableLoader ? (
          <AdminAssetManagementSkeleton />
        ) : (
          <>
            <TableContainer sx={{ minHeight: 480 }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead sx={{ bgcolor: (theme) => theme.palette.table.headBg }}>
                  <TableRow>
                    {[
                      "Serial No.",
                      "Owner",
                      "Asset",
                      "Category",
                      "Value",
                      "Investors",
                      "Status",
                      "Created At",
                      "Actions",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: (theme) => theme.palette.table.headText,
                          py: 2,
                          borderBottom: (theme) =>
                            `1px solid ${theme.palette.border.subtle}`,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ color: "text.muted" }}>No assets found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r, rowIndex) => (
                      <TableRow
                        key={r.id || r.name}
                        hover
                        onClick={() => {
                          if (!r.id) return;
                          navigate(`/admin/assets/${r.id}`);
                        }}
                        sx={{
                          cursor: r.id ? "pointer" : "default",
                          "&:hover": {
                            bgcolor: (theme) => theme.palette.table.rowHover,
                          },
                          "& td": {
                            borderColor: (theme) => theme.palette.border.subtle,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            py: 2,
                            fontSize: 14,
                            fontWeight: 600,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(page - 1) * limit + rowIndex + 1}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar src={r.ownerAvatar} sx={{ width: 36, height: 36, fontSize: 14 }}>
                              {ownerInitials(r.owner)}
                            </Avatar>
                            <Box>
                              <Typography fontSize={14} sx={{ color: "text.primary" }}>
                                {r.owner}
                              </Typography>
                              {r.ownerOthers > 0 && (
                                <Typography variant="caption" sx={{ color: "text.muted" }}>
                                  +{r.ownerOthers} others
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box>
                            <Typography fontSize={14} fontWeight={700} sx={{ color: "text.primary" }}>
                              {r.name}
                            </Typography>
                            {/* <Stack direction="row" alignItems="center" spacing={0.5}>
                              <LocationOn sx={{ fontSize: 12, color: "text.muted" }} />
                              <Typography variant="body2" fontSize={13} sx={{ color: "text.muted" }}>
                                {r.location}
                              </Typography>
                            </Stack> */}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <CategoryBadge label={r.category} />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={14} fontWeight={700} sx={{ color: "text.primary" }}>
                            {formatSar(r.value)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <InvestorsCell count={r.investorsCount} investors={r.investors} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={r.status} />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={14} sx={{ color: "text.primary" }}>
                            {r.date !== "—" ? r.date : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (r.id) navigate(`/admin/assets/${r.id}`);
                                else openReview(r);
                              }}
                              sx={{
                                color: (theme) => theme.palette.accent.teal.action,
                                "&:hover": {
                                  bgcolor: (theme) => theme.palette.accent.teal.chipBg,
                                },
                              }}
                              aria-label="View asset"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                            {r.status === STATUS.UNDER_REVIEW && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuickApprove(r)}
                                  disabled={approveMutationLoading}
                                  sx={{
                                    color: (theme) => theme.palette.chips.success.text,
                                    "&:hover": {
                                      bgcolor: (theme) => theme.palette.chips.success.bg,
                                    },
                                  }}
                                  aria-label="Approve asset"
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => openReject(r)}
                                  sx={{
                                    color: (theme) => theme.palette.chips.error.text,
                                    "&:hover": {
                                      bgcolor: (theme) => theme.palette.chips.error.bg,
                                    },
                                  }}
                                  aria-label="Reject asset"
                                >
                                  <HighlightOff fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={Math.max(total, rows.length)}
              page={Math.max(0, page - 1)}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={limit}
              onRowsPerPageChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Rows per page"
            />
          </>
        )}
      </Paper>

      <AssetReviewModal
        open={Boolean(reviewAsset)}
        onClose={closeReview}
        asset={reviewAsset}
        onVerify={handleVerify}
        onRejectClick={(a) => openReject(a)}
      />

      <RejectAssetModal
        open={Boolean(rejectAsset)}
        onClose={closeReject}
        asset={rejectAsset}
        onConfirm={handleRejectConfirm}
      />
    </Box>
  );
}
